import { lookupRhSection } from "./rhSteelTable";

export interface WeightResult {
  areaMm2: number;
  unitWeightKgM: number;
}

function parseSpec(spec: string): number[] {
  const parts = spec.split(/[Xx＊*×]/).map((s) => parseFloat(s.trim()));
  return parts.filter((n) => !isNaN(n));
}

export function calculateSteelWeight(
  steelType: string,
  spec: string,
): WeightResult | null {
  if (steelType === "RH") {
    const section = lookupRhSection(spec);
    if (section) {
      return {
        areaMm2: Math.round(section.areaCm2 * 100 * 100) / 100,
        unitWeightKgM: section.unitWeightKgM,
      };
    }
  }

  const dims = parseSpec(spec);
  if (dims.length === 0) return null;

  let areaMm2 = 0;

  switch (steelType) {
    case "TUBE":
      if (dims.length >= 3) {
        const [B, H, t] = dims;
        if (t > B / 2 || t > H / 2) return null;
        areaMm2 = B * H - (B - 2 * t) * (H - 2 * t);
      }
      break;

    case "BOX":
      if (dims.length >= 4) {
        const [H, B, tw, tf] = dims;
        areaMm2 = H * B - (H - 2 * tf) * (B - 2 * tw);
      }
      break;

    case "PIPE":
      if (dims.length >= 2) {
        const [D, t] = dims;
        if (t > D / 2) return null;
        areaMm2 = (Math.PI / 4) * (D * D - (D - 2 * t) * (D - 2 * t));
      }
      break;

    case "RB":
      if (dims.length >= 1) {
        const [D] = dims;
        areaMm2 = (Math.PI / 4) * D * D;
      }
      break;

    case "FB":
      if (dims.length >= 2) {
        const [t, B] = dims;
        areaMm2 = B * t;
      }
      break;

    case "L":
      if (dims.length >= 3) {
        const [A, B, t] = dims;
        if (A === B) {
          areaMm2 = 2 * A * t - t * t;
        } else {
          areaMm2 = A * t + B * t - t * t;
        }
      }
      break;

    case "H":
    case "RH":
    case "BH":
    case "I":
      if (dims.length >= 4) {
        const [H, B, tw, tf] = dims;
        areaMm2 = 2 * B * tf + (H - 2 * tf) * tw;
      }
      break;

    case "U":
      if (dims.length >= 4) {
        const [H, B, tw, tf] = dims;
        areaMm2 = H * tw + 2 * B * tf - 2 * tw * tf;
      }
      break;

    case "T":
    case "BT":
    case "CT":
      if (dims.length >= 4) {
        const [H, B, tw, tf] = dims;
        areaMm2 = B * tf + (H - tf) * tw;
      }
      break;

    case "C":
      if (dims.length >= 4) {
        const [H, B, C, t] = dims;
        areaMm2 = (H - 2 * t) * t + 2 * (B - t) * t + 2 * (C - t) * t;
      }
      break;

    default:
      return null;
  }

  if (areaMm2 <= 0) return null;

  const unitWeightKgM = areaMm2 * 0.00785;

  return {
    areaMm2: Math.round(areaMm2 * 100) / 100,
    unitWeightKgM: Math.round(unitWeightKgM * 1000) / 1000,
  };
}

type NumberLike = number | string | null | undefined;

function toFiniteNumber(value: NumberLike) {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

export const UNIT_WEIGHT_DECIMALS = 1;
export const LENGTH_DECIMALS = 3;
export const WEIGHT_DECIMALS = 1;

export function roundUnitWeight(value: NumberLike) {
  const num = toFiniteNumber(value);
  return num === null ? null : Math.round(num * 10) / 10;
}

export function roundLength(value: NumberLike) {
  const num = toFiniteNumber(value);
  return num === null ? null : Math.round(num * 1000) / 1000;
}

export function formatUnitWeight(value: NumberLike) {
  const num = roundUnitWeight(value);
  return num === null ? "" : num.toFixed(UNIT_WEIGHT_DECIMALS);
}

export function matchesDerivedUnitWeight(
  derived: NumberLike,
  stored: NumberLike,
) {
  const derivedNum = roundUnitWeight(derived);
  const storedNum = toFiniteNumber(stored);
  if (derivedNum === null || storedNum === null) return false;
  return (
    derivedNum === storedNum ||
    derivedNum === roundUnitWeight(storedNum) ||
    Math.round(derivedNum) === storedNum
  );
}

export function formatLength(value: NumberLike) {
  const num = toFiniteNumber(value);
  return num === null ? "" : num.toFixed(LENGTH_DECIMALS);
}

export function formatWeight(value: NumberLike) {
  const num = toFiniteNumber(value);
  return num === null ? "" : num.toFixed(WEIGHT_DECIMALS);
}

export function steelWeight(unitWeight: NumberLike, length: NumberLike) {
  const uw = roundUnitWeight(unitWeight);
  const len = roundLength(length);
  if (uw === null || len === null) return null;
  return Math.round(uw * len * 10) / 10;
}
